import * as types from "../../mutation-types";
import check from "../../../domains2/libraries/check-types-ext";
import * as utils from "../../../libraries/utils";
import appConfig from "../../../appconfig";
import Vue from "vue";
import _ from "lodash";
import {DateTime} from "luxon";
import OrderHelperObject from "../../../domains2/orders/OrderHelperObject";
import ProductSelection from "../../../domains2/orders/ProductSelection";
import { OrderLineType } from "../../../domains2/orders/OrderLine2";
import {DomainCoreGeneration} from "../../../domains2/libraries/DomainCoreGeneration";
import {evaluateCustomerType} from "../../../libraries/utils";
import { convertProductSelectionsToMap } from "../../../domains2/orders/OrderLineMappingHelper";

const getDataSource = state => state.persistedData.orderEditMode ? "editModeData" : "newCartData";

const tenantConfig = appConfig.tenantConfig;

const CartMutations = {
	[types.SET_CUSTOMER_TRACKING_TAG] (state, value) {
		state.persistedData.customerTrackingTagData = value;
	},
	[types.SET_ESTIMATED_POINTS] (state, value = 0) {
		state.estimatedPoints = value;
	},
	[types.SET_DISCOUNT_CAPPED_STATE] (state, value = null) {
		state.discountCapped = value;
	},
	[types.SET_SELECTED_DEAL_ID] (state, value = null) {
		state.persistedData.selectedDealId = value;
	},
	[types.SET_CURRENT_CATALOG_PAGE] (state, value = null) {
		state.currentCatalogPageExtId = value;
	},
	[types.SET_CURRENT_PRODUCT] (state, value = null) {
		state.currentProductId = value;
	},
	[types.SET_RETROACTIVE_POINTS_ASSIGNMENT_PENDING_ORDER_ID] (state, value = null) {
		state.persistedData.retroactivePointsAssignmentPendingOrderId = value;
	},
	[types.SET_EMPLOYEE_DISCOUNT_TOKEN] (state, value = false) {
		state.persistedData.employeeDiscountToken = value;
	},
	[types.PRODUCT_CATEGORY_ID] (state, value= null) {
		state.persistedData.productCategoryId = value;
	},
	[types.SET_UPSELL_MODAL_STATE] (state, value = false) {
		state.isUpsellModalOpened = value;
	},
	[types.SET_MYSTERY_BOX_MODAL_STATE] (state, value = false) {
		state.persistedData.shouldShowMysteryBox = value;
	},
	[types.SET_BASKET_STATE_ON_ONE_STEP_FLOW_WITH_FOUR_COLUMNS] (state, value = false) {
		state.persistedData.shouldHideBasketOneStepFlowFourColumns = value;
	},
	[types.SET_REORDER_STATE] (state, payload) {
		state.persistedData.isReorder = payload && payload.isReorder || false;
		state.persistedData.originalProductSelectionsFromPreviousOrder = payload && payload.filteredProductSelections || [];
		if(!payload) {
			state.persistedData.originalPreviousOrderTotal = 0;
		}
	},
	[types.SET_PREVIOUS_ORDER_TOTAL] (state, total) {
		state.persistedData.originalPreviousOrderTotal = total;
	},
	[types.SHOW_EMPLOYEE_DISCOUNT_SUCCESS_MESSAGE] (state, value = false) {
		state.showEmployeeDiscountSuccessMessage = value;
	},
	[types.SET_RECOMMENDED_PRODUCTS] (state, value = []) {
		state.recommendedProducts = value;
	},
	[types.SET_RECOMMENDED_PRODUCTS_FOR_CATALOG_PAGE] (state, value = []) {
		state.persistedData.recommendedProductsCatalogPage = value;
	},
	[types.SET_PICKED_RECOMMENDED_PRODUCTS] (state, value = []) {
		state.pickedRecommendedProducts = value;
	},
	[types.SET_EXPERIMENT_ID] (state, value = null) {
		state.experimentId = value;
	},
	[types.ADD_TO_CART] (state, { productSelection }) {
		const dataSource = getDataSource(state);
		const productSelections = Array.isArray(productSelection) ? productSelection : [productSelection];

		// warning for state management
		if (productSelections.length > 1) {
			// eslint-disable-next-line no-console
			console.warn("Adding multiple product selections to cart one by one causes big performance issues when rendering DOM. Please consider using ADD_TO_CART_BULK mutation instead!");
		}

		productSelections.forEach(ps => {
			check.object.orFail(ps);

			const existingProductSelection = state.persistedData[dataSource].productSelections.find(item => item.productId === ps.productId);
			const duplicateProductSelection = state.persistedData[dataSource].productSelections.find(item => item.productSelectionId !== undefined && item.productSelectionId === ps.productSelectionId);
			if(duplicateProductSelection || (existingProductSelection && ps.forceNewInCart !== true)) {
				Object.assign(existingProductSelection, ps);
			} else {
				if(appConfig.channelAlias() === "kiosk" || appConfig.channelAlias() === "desktopBrowser"){
					state.persistedData[dataSource].productSelections.unshift(Object.assign({}, ps));
				} else {

					if (!state.persistedData[dataSource].groupOrderId) {
						state.persistedData[dataSource].productSelections.push(Object.assign({}, ps));
						return;
					}
					// handle cases for shared basket *sunset share feature)
					// 1) when there are no product selections from group, yet, just push selections to the end
					const groupData = state.persistedData.newCartData.groupOrderFriendsOrderData;
					const psRef = state.persistedData.newCartData.productSelections;
					if (!groupData.length) {
						psRef.push(ps);
					} else {
						// 2) when groupOrderFriendsOrderData is not empty, and when selections to be added in basket are picked by user (not group friends), then insert selections at the last selection which is added in basket by that same user. So insert will happen after the last user selection but before first group friends selection
						const friendsSelectionIds = groupData.map(x=>x.productSelections.map(y=>y.productSelectionId)).flat();
						const insertAt = Math.abs(psRef.length - friendsSelectionIds.length);
						psRef.splice(insertAt, 0, ps);
					}
				}
			}
		});
	},

	[types.ADD_TO_CART_BULK] (state, { productSelections, isGroupOrderSelections = false }) {
		const dataSource = getDataSource(state);
		if(appConfig.channelAlias() === "kiosk" || appConfig.channelAlias() === "desktopBrowser"){
			state.persistedData[dataSource].productSelections.unshift(..._.cloneDeep(productSelections));
		} else {
			if (!state.persistedData[dataSource].groupOrderId) {
				state.persistedData[dataSource].productSelections.push(..._.cloneDeep(productSelections));
				return;
			}
			// handle cases for shared basket *sunset share feature)
			// 1) when there are no product selections from group, yet, just push selections to the end. Two cases:
			//   - when groupOrderFriendsOrderData is empty, always add to end
			//   - when selections are actually coming from friends, always add to end
			const groupData = state.persistedData.newCartData.groupOrderFriendsOrderData;
			const psRef = state.persistedData.newCartData.productSelections;
			if (!groupData.length || isGroupOrderSelections || !psRef.length) {
				psRef.push(..._.cloneDeep(productSelections));
			} else {
				// 2) when groupOrderFriendsOrderData is not empty, and when selections to be added in basket are picked by user (not group friends), then insert selections at the last selection which is added in basket by that same user. So insert will happen after the last user selection but before first group friends selection
				psRef.splice(psRef.length, 0, ..._.cloneDeep(productSelections));
			}
		}
	},

	[types.REMOVE_FROM_CART_BULK] (state, { productSelections }) {
		const dataSource = getDataSource(state);
		const productSelectionIds = productSelections.map(ps => ps.productSelectionId);
		state.persistedData[dataSource].productSelections =
			state.persistedData[dataSource].productSelections.filter(ps => {
				return !productSelectionIds.includes(ps.productSelectionId);
			});
	},

	[types.REMOVE_FROM_CART] (state, { id }) {
		const dataSource = getDataSource(state);
		// removes all product instances from cart
		state.persistedData[dataSource].productSelections =
			state.persistedData[dataSource].productSelections.filter(ps => ps.productId !== id);
	},

	[types.REMOVE_FROM_CART_SUB_PRODUCT](state, {product: {rootId, productId}}) {
		const dataSource = getDataSource(state);
		const getIndexToReplace = (item) => {
			return item.findIndex(product => product.selectedProducts.some(selected => {
				if(selected.productId){
					return selected.productId === productId;
				}
				return selected === productId;
			}));
		};
		const newProductSelections = state.persistedData[dataSource].productSelections.map((product) => {
			if (product.productId === rootId) {
				return {
					...product,
					groups: product.groups.map((groupProduct, index) => {
						if (index === getIndexToReplace(product.groups)) {
							return {
								...groupProduct,
								selectedProducts: groupProduct.selectedProducts.filter(selectedProduct => {
									if(selectedProduct.productId){
										return selectedProduct.productId !== productId;
									}
									return selectedProduct !== productId;
								})
							};
						}
						return {
							...groupProduct
						};
					})
				};
			}
			return product;
		});

		state.persistedData[dataSource].productSelections = state.persistedData[dataSource].productSelections.map(product => {
			if (product.productId === rootId) {
				const productToSave = newProductSelections.find(newProduct => newProduct.productId === rootId);
				return productToSave ? productToSave : product;
			}
			return product;
		});
	},

	[types.REMOVE_FROM_CART_BY_PRODUCT_SELECTION_ID] (state, { productSelectionId }) {
		const dataSource = getDataSource(state);
		// removes all product instances from cart
		state.persistedData[dataSource].productSelections =
			state.persistedData[dataSource].productSelections.filter(ps => ps.productSelectionId !== productSelectionId);
	},

	[types.REMOVE_FROM_CART_WITHOUT_CHECKOUT_RESET] (state, { productSelectionId }) {
		const dataSource = getDataSource(state);
		// removes all product instances from cart
		state.persistedData[dataSource].productSelections =
			state.persistedData[dataSource].productSelections.filter(ps => ps.productSelectionId !== productSelectionId);
	},
	[types.CART_EMPTY_ALL] (state) {
		const dataSource = getDataSource(state);
		// removes all product from cart
		state.persistedData.sunsetPoints = null;
		state.persistedData.singleMainProductIdsToMenuIdsMap = null;
		state.persistedData.productSelectionIdToRemove = [];
		state.persistedData.upgradeToMenuFromBasketFlag = false;
		state.persistedData[dataSource].productSelections = [];
		state.persistedData[dataSource].cartOrderId = null;
	},

	[types.PURGE_CART](state) {
		const dataSource = getDataSource(state);
		// removes all product from cart
		state.persistedData.sunsetPoints = null;
		state.persistedData.friendInviteCode = undefined;
		state.persistedData.menuProductIdsForReselection = [];
		state.persistedData.unavailableProductIds = [];
		state.persistedData.singleMainProductIdsToMenuIdsMap = null;
		state.persistedData.productSelectionIdToRemove = [];
		state.persistedData.upgradeToMenuFromBasketFlag = false;
		state.persistedData.ppdProductSelectionIds = [];

		state.persistedData[dataSource] = {
			cartOrderId: null,
			specialNotes: "",
			userConsent: false,
			productSelections: [],
			checkoutStep: 0,
			checkoutStepGt3: 0,
			cartUserDataSubmitted: false,
			cartSmsVerified: false,
			shopData: { submitted: false },
			pickupData: { submitted: false },
			orderSent: false,
			orderIsPrepaid: false,
			lastOrderNumber: false,
			lastOrderPrice: 0,
			lastOrderPreTotalPriceWithReturnableItems: 0,
			lastOrderPriceWithReturnableItems: 0,
			lastOrderTotalPriceWithoutDiscount: 0,
			lastOrderProductIds: [],
			lastOrderReceiptLines: [],
			productCategory: [],
			lastOrderIsHeated: false,
			lastOrderTenantSpecificData: {},
			isOrderChanged: false,
			pixelPurchaseFired: false,
			mustBeHeated: false,
			asapPickup: false,
			tenantSpecificData: {},
			groupOrderFriendsOrderData: [],
			qrCodeTableServingData: {
				tableNumber: undefined,
				isQrCodeTableServing: undefined
			},
			meta: {}
		};

		state.rcmMarketingCard = {
			productIds: [],
			enable: false
		};
	},

	[types.UPDATE_CART_QUANTITY] (state, { id, quantity }) {
		const dataSource = getDataSource(state);
		// update product selection quantity
		const productSelection = state.persistedData[dataSource].productSelections.find(item => item.productId === id);
		if (productSelection) {
			productSelection.quantity = quantity;
		}
	},

	[types.UPDATE_CART_QUANTITY_BY_ID] (state, { productSelectionId, quantity }) {
		const dataSource = getDataSource(state);
		// update product selection quantity
		const productSelection = state.persistedData[dataSource].productSelections.find(item => item.productSelectionId === productSelectionId);
		if (productSelection) {
			productSelection.quantity = quantity;
		}
	},

	[types.UPDATE_CART_NAME] (state, { id, name }) {
		const dataSource = getDataSource(state);
		const productSelection = state.persistedData[dataSource].productSelections.find(item => item.productId === id);
		// update product selection name
		if (productSelection) {
			productSelection.name = name;
		}
	},

	[types.UPDATE_CART_SUBORDERLINES] (state, { id, subOrderLines }) {
		const dataSource = getDataSource(state);
		const productSelection = state.persistedData[dataSource].productSelections.find(item => item.productId === id);
		if (productSelection) {
			productSelection.subOrderLines = subOrderLines;
		}
	},

	[types.UPDATE_SPECIAL_NOTES] (state, specialNote) {
		const dataSource = getDataSource(state);
		// update special notes
		state.persistedData[dataSource].specialNotes = specialNote;
	},

	[types.UPDATE_USER_CONSENT] (state, userConsent) {
		const dataSource = getDataSource(state);
		// update special notes
		state.persistedData[dataSource].userConsent = userConsent;
	},

	[types.CART_INCREASE_STEP] (state, {currentStep}) {
		check.string.orFail(currentStep, "currentStep");

		const dataSource = getDataSource(state);

		const hooks = {
			stepCheckoutInit() {
				return state.persistedData[dataSource].productSelections.length > 0;
			},
			stepUserDataWrapper() {
				const smsVerificationNeeded = function () {
					const totalPrice = state.persistedData[dataSource].productSelections.reduce((carry, selection) => {
						return carry + (selection.quantity * selection.price);
					}, 0);
					const totalVat = utils.calculatePercentage(totalPrice, tenantConfig.vat);

					return (totalPrice + totalVat) >= tenantConfig.smsVerificationLimit;
				};

				if (state.persistedData[dataSource].cartUserDataSubmitted) {
					// check if sms verification is needed
					if (smsVerificationNeeded()) {
						if (state.persistedData[dataSource].cartSmsVerified) {
							return true;
						}
					} else {
						return true;
					}
				}

				return false;
			},
			stepShop() {
				if (state.persistedData[dataSource].shopData.submitted) {
					// if shop does not offer heated food then remove it from state
					if (state.persistedData[dataSource].shopData.offersHeatableFood !== true) {
						state.persistedData[dataSource].mustBeHeated = false;
					}

					return true;
				}

				return false;
			},
			stepPickupData() {
				if (state.persistedData[dataSource].pickupData.submitted) {
					return true;
				}

				return false;
			},
			stepFinishWrapper() {
				return true;
			},
			stepConfirm() {
				return true;
			},
			shoppingCartStepCheckoutInit() {
				return true;
			},
			awaitingConfirmation() {
				return true;
			},
			orderSent() {
				return true;
			}
		};

		if (hooks[currentStep]() === true) {
			if (currentStep === "stepFinishWrapper") {
				state.persistedData[dataSource].checkoutStep = 2;
			} else {
				state.persistedData[dataSource].checkoutStep++;
			}
		}
	},

	[types.CART_DECREASE_STEP] (state, {destinationStep}) {
		check.string.orFail(destinationStep, "destinationStep");
		const dataSource = getDataSource(state);

		const hooks = {
			stepCheckoutInit() {

			},
			stepUserData() {
				state.persistedData[dataSource].cartUserDataSubmitted = false;
			},
			stepUserDataWrapper() {
				state.persistedData[dataSource].cartUserDataSubmitted = false;
			},
			stepShop() {
				state.persistedData[dataSource].shopData.submitted = false;
			},
			stepPickupData() {
				state.persistedData[dataSource].pickupData.submitted = false;
			},
			stepFinishWrapper() {

			},
			shoppingCartStepCheckoutInit() {

			},
			awaitingConfirmation() {

			},
			orderSent() {

			}
		};

		hooks[destinationStep]();
		state.persistedData[dataSource].checkoutStep--;
	},
	[types.CART_INCREASE_STEP_GT3] (state) {

		const dataSource = getDataSource(state);

		// if (hooks[currentStep]() === true) {
		state.persistedData[dataSource].checkoutStepGt3++;
		// }
	},

	[types.CART_DECREASE_STEP_GT3] (state) {
		const dataSource = getDataSource(state);

		state.persistedData[dataSource].checkoutStepGt3--;
	},

	[types.CART_RESET_STEP_GT3] (state) {
		const dataSource = getDataSource(state);

		state.persistedData[dataSource].checkoutStepGt3 = 0;
	},

	[types.CART_SUBMIT_USER_DATA] (state) {
		const dataSource = getDataSource(state);
		state.persistedData[dataSource].cartUserDataSubmitted = true;

	},

	[types.CART_USER_DATA_SET](state, {name, email, tel}) {
		const dataSource = getDataSource(state);
		state.persistedData[dataSource].customerDetails.name = name;
		state.persistedData[dataSource].customerDetails.email = email;
		state.persistedData[dataSource].customerDetails.telephone = tel;
	},

	[types.CART_SET_SHOP_DATA] (state, shopData) {
		const dataSource = getDataSource(state);

		state.persistedData[dataSource].shopData = {
			...shopData,
			submitted: !!shopData.submitted
		};
	},

	[types.CART_SET_PICKUP_DATA] (state, pickupData) {
		const dataSource = getDataSource(state);
		const source = state.persistedData[dataSource];
		const { pickupTime, pickupDate, ...restPickupData } = pickupData || {};
		if (pickupTime !== source.pickupTime) {
			source.pickupTime = pickupTime;
		}
		if (pickupDate !== source.pickupDate) {
			source.pickupDate = pickupDate;
		}

		source.pickupData = {
			...restPickupData,
			submitted: !!pickupData.submitted
		};
	},

	[types.CHECKOUT_RESET] (state) {
		const dataSource = getDataSource(state);

		state.persistedData[dataSource].checkoutStep = 0;
		state.persistedData[dataSource].cartUserDataSubmitted = false;
		state.persistedData[dataSource].shopData.submitted = false;
		state.persistedData[dataSource].pickupData.submitted = false;
		state.persistedData[dataSource].orderSent = false;
		// state.persistedData[dataSource].orderIsPrepaid = false;
	},

	[types.CHECKOUT_ORDER_SENT] (
		state,
		{
			orderNumber,
			orderIsPrepaid = false,
			totalAmountCents = 0,
			productCatalog,
			featureToggles,
			applicationUserData
		}) {
		const dataSource = getDataSource(state);

		state.persistedData[dataSource].orderSent = true;
		state.persistedData[dataSource].orderIsPrepaid = orderIsPrepaid;
		state.persistedData[dataSource].lastOrderNumber = orderNumber;
		// Total Prices
		state.persistedData[dataSource].lastOrderPrice = OrderHelperObject.getTotalPrice(
			state.persistedData[dataSource].productSelections,
			productCatalog,
			{
				options: {
					vouchers: state.persistedData[dataSource].vouchersRedeemed,
					loyaltySystemDiscount: state.persistedData[dataSource].tenantSpecificData && state.persistedData[dataSource].tenantSpecificData.loyaltySystemDiscount,
					employeeDiscountToken: state.persistedData.employeeDiscountToken,
					sunsetPoints: state.persistedData.sunsetPoints,
					dailySpending: featureToggles.discountDailyLimit ? applicationUserData
						&& applicationUserData.meta
						&& applicationUserData.meta.dailySpending : {},
					channel: appConfig.channelAlias(),
					deliveryMethod: state.persistedData[dataSource].tenantSpecificData.deliveryChoice,
					customerType: evaluateCustomerType(),
					cartProductsMap: convertProductSelectionsToMap(state.persistedData[dataSource].productSelections),
					enableProductBestPriceInCatalogAndCart: featureToggles.enableProductBestPriceInCatalogAndCart
				},
				orderTags: state.persistedData[dataSource].cartOrderTags,
				generation: DomainCoreGeneration.getGenerationByBMId(appConfig.getBusinessModelId())
			}
		);

		state.persistedData[dataSource].lastOrderPriceWithReturnableItems = OrderHelperObject.getTotalPrice(
			state.persistedData[dataSource].productSelections,
			productCatalog,
			{
				includeReturnableItems: state.persistedData[dataSource].mustBeHeated,
				options: {
					vouchers: state.persistedData[dataSource].vouchersRedeemed,
					...state.persistedData[dataSource].tenantSpecificData

				},
				orderTags: state.persistedData[dataSource].cartOrderTags,
				generation: DomainCoreGeneration.getGenerationByBMId(appConfig.getBusinessModelId())
			}
		);

		state.persistedData[dataSource].lastOrderPreTotalPriceWithReturnableItems = OrderHelperObject.getTotalPrice(
			state.persistedData[dataSource].productSelections,
			productCatalog,
			{
				includeReturnableItems: state.persistedData[dataSource].mustBeHeated,
				options: {
					...state.persistedData[dataSource].tenantSpecificData
				},
				applyPriceRules: false,
				orderTags: state.persistedData[dataSource].cartOrderTags,
				generation: DomainCoreGeneration.getGenerationByBMId(appConfig.getBusinessModelId())
			}
		);
		//

		const orderLines = ProductSelection.mapToOrderLinesGen(
			DomainCoreGeneration.getGenerationByBMId(appConfig.getBusinessModelId())
		)(
			productCatalog,
			state.persistedData[dataSource].productSelections,
			state.persistedData[dataSource].mustBeHeated,
			{
				vouchers: state.persistedData[dataSource].vouchersRedeemed,
				...state.persistedData[dataSource].tenantSpecificData,
				dailySpending: featureToggles.discountDailyLimit ? applicationUserData
					&& applicationUserData.meta
					&& applicationUserData.meta.dailySpending : {},
				customerType: evaluateCustomerType(),
				cartProductsMap: convertProductSelectionsToMap(state.persistedData[dataSource].productSelections),
				enableProductBestPriceInCatalogAndCart: featureToggles.enableProductBestPriceInCatalogAndCart
			},
			state.persistedData[dataSource].cartOrderTags
		);

		state.persistedData[dataSource].lastOrderTotalPriceWithoutDiscount = OrderHelperObject.getTotalPriceWithoutDiscount(
			orderLines.filter(el => el.type !== OrderLineType.Delivery), false
		);

		state.persistedData[dataSource].lastOrderProductIds = OrderHelperObject.getProductIds({
			productSelections : state.persistedData[dataSource].productSelections,
			includeDisabled : false,
			productCatalog
		});

		state.persistedData[dataSource].lastOrderIsHeated = state.persistedData[dataSource].mustBeHeated;
		state.persistedData[dataSource].lastOrderReceiptLines = OrderHelperObject.mapToReceiptLines(orderLines);
		state.persistedData[dataSource].productCategory = state.persistedData.newCartData.productSelections;
		state.persistedData[dataSource].lastOrderTenantSpecificData = state.persistedData[dataSource].tenantSpecificData;
		// determine if this is an additional payment
		const isOrderChanged = state.persistedData[dataSource].lastOrderPriceWithReturnableItems === 0 ||
			Math.round(state.persistedData[dataSource].lastOrderPriceWithReturnableItems * 100) / 100 !== (Number.parseInt(totalAmountCents) / 100);
		state.persistedData[dataSource].isOrderChanged = isOrderChanged;

		// reset the cart
		state.persistedData[dataSource].productSelections = [];
		state.persistedData[dataSource].specialNotes = "";
		state.persistedData[dataSource].userConsent = false;
		state.persistedData[dataSource].mustBeHeated = false;
		state.persistedData[dataSource].pixelPurchaseFired = false;
		state.persistedData[dataSource].vouchersRedeemed = [];
		state.persistedData[dataSource].tenantSpecificData = {};
	},

	[types.FACEBOOK_PIXEL_PURCHASE] (state) {
		const dataSource = getDataSource(state);

		state.persistedData[dataSource].pixelPurchaseFired = true;
	},

	[types.ADD_VOUCHER_TO_ORDER] (state, voucherId) {
		const dataSource = getDataSource(state);

		const vouchersRedeemed = state.persistedData[dataSource].vouchersRedeemed || [voucherId];
		if(Array.isArray(vouchersRedeemed) && !vouchersRedeemed.includes(voucherId)) {
			vouchersRedeemed.push(voucherId);
		}

		state.persistedData[dataSource] = {
			...state.persistedData[dataSource],
			vouchersRedeemed
		};
	},

	[types.REMOVE_VOUCHER_FROM_ORDER] (state, voucherId) {
		const dataSource = getDataSource(state);

		if(Array.isArray(state.persistedData[dataSource].vouchersRedeemed) && state.persistedData[dataSource].vouchersRedeemed.length > 0) {
			state.persistedData[dataSource].vouchersRedeemed = state.persistedData[dataSource].vouchersRedeemed.filter(v => v !== voucherId);
		}
	},

	[types.CLEAR_VOUCHERS_FROM_ORDER] (state) {
		const dataSource = getDataSource(state);

		if(Array.isArray(state.persistedData[dataSource].vouchersRedeemed) && state.persistedData[dataSource].vouchersRedeemed.length > 0) {
			state.persistedData[dataSource].vouchersRedeemed = [];
		}
	},

	[types.SET_VOUCHER_VIRTUAL_ORDER_ID] (state, voucherVirtualOrderId = null) {
		const dataSource = getDataSource(state);
		state.persistedData[dataSource].voucherVirtualOrderId = voucherVirtualOrderId;
	},

	[types.SET_CACHED_SHOPS] (state, shops) {
		state.cachedShops = shops;
	},

	[types.SET_CACHED_PICKUP_RULES] (state, pickupRules) {
		state.cachedPickupRules = pickupRules;
	},

	[types.MUST_BE_HEATED] (state, mustBeHeatedValue) {
		const dataSource = getDataSource(state);
		state.persistedData[dataSource].mustBeHeated = mustBeHeatedValue;
	},

	[types.SET_ASAP_PICKUP] (state, asapPickupValue) {
		const dataSource = getDataSource(state);
		state.persistedData[dataSource].asapPickup = asapPickupValue;
	},
	[types.SET_META] (state, meta = {}) {
		const dataSource = getDataSource(state);
		state.persistedData[dataSource].meta = meta;
	},
	[types.SET_CART_ORDER_ID] (state, id) {
		const dataSource = getDataSource(state);
		state.persistedData[dataSource].cartOrderId = id;
	},
	[types.SET_CART_ORDER_TAGS](state, tags) {
		const dataSource = getDataSource(state);
		Vue.set(state.persistedData[dataSource], "cartOrderTags", [...tags]);
	},

	[types.ENABLE_ORDER_EDIT_MODE] (state, { order, shop, editType, checkoutStep }) {
		state.persistedData.orderEditMode = true;
		state.persistedData.orderEditType = editType;

		state.persistedData.editModeData = Object.assign(state.persistedData.editModeData, {
			checkoutStep: checkoutStep,
			cartOrderId: order.extId,
			specialNotes: order.specialNotes,
			productSelections: order.productSelections,
			cartUserDataSubmitted: false,
			// cartUserDataSubmitted: checkoutStep > (tenantConfig.tenantId === 3 ? 2 : 1), // for foetex steps are harcoded in checkoutStep in cartGetters
			shopData: {
				id: shop.id,
				shopAddress: shop.address,
				shopName: shop.name,
				offersHeatableFood: shop.offersHeatableFood,
				submitted: checkoutStep > 2,
				shopWorkingHours: shop.shopWorkingHours
			},
			pickupDate: order.pickupDate,
			pickupTime: order.pickupTime,
			pickupData: {
				submitted: checkoutStep > 3
			},
			orderIsPrepaid: order.paymentStatus === "prepaid",
			mustBeHeated: order.orderOptions.mustBeHeated,
			paymentLinkThroughEmail: order.orderOptions.paymentLinkThroughEmail,
			tenantSpecificData: { deliveryChoice: order.orderOptions.deliveryChoice, driveInDelivery: order.orderOptions.driveInDelivery }
		});

		// we need this not be passed by reference so we are making a clone
		state.persistedData.orderEditOriginalOrder = JSON.parse(JSON.stringify(order));

		return checkoutStep;
	},

	[types.DISABLE_ORDER_EDIT_MODE] (state) {
		state.persistedData.orderEditMode = false;
		state.persistedData.orderEditType = null;
		state.persistedData.orderEditOriginalOrder = null;

		state.persistedData.editModeData = {
			cartOrderId: null,
			checkoutStep: 0,
			checkoutStepGt3: 0,
			specialNotes: "",
			userConsent: false,
			cartUserDataSubmitted: false,
			productSelections: [],
			shopData: {
				submitted: false
			},
			pickupData: {
				submitted: false
			},
			orderIsPrepaid: false,
			mustBeHeated: false,
			tenantSpecificData: {}
		};
	},
	[types.SET_DISCOUNT_CODE] (state, discount) {
		state.persistedData.discount = {...discount};
	},
	[types.SET_TENANT_SPECIFIC_DATA] (state, {key, value}) {
		const dataSource = getDataSource(state);
		// eslint-disable-next-line no-unused-vars
		const {[key]: _ , ...updatedTenantSpecificData} = state.persistedData[dataSource].tenantSpecificData;
		state.persistedData[dataSource].tenantSpecificData = { ...updatedTenantSpecificData, [key]: value };
	},
	[types.DELETE_TENANT_SPECIFIC_DATA] (state) {
		const dataSource = getDataSource(state);
		state.persistedData[dataSource].tenantSpecificData = {};
	},
	[types.REMOVE_FROM_TENANT_SPECIFIC_DATA] (state, key) {
		const dataSource = getDataSource(state);
		// eslint-disable-next-line no-unused-vars
		const {[key]: _ , ...updatedTenantSpecificData} = state.persistedData[dataSource].tenantSpecificData;
		state.persistedData[dataSource].tenantSpecificData = updatedTenantSpecificData;
	},
	[types.SET_LAST_UPDATE_TIME] (state) {
		state.persistedData.lastUpdateTime = DateTime.now();
	},
	[types.ADD_TEMPORARY_ADDRESS_TO_PERMANENT_ADDRESS] (state, deliveryDetailsObj) {
		state.persistedData.deliveryDetails = deliveryDetailsObj;
	},
	[types.ADD_DELIVERY_ADDRESS_AND_FLOOR] (state, deliveryObject) {
		state.persistedData.deliveryDetails = {
			...state.persistedData.deliveryDetails,
			address: deliveryObject.address,
			doorFloor: deliveryObject.doorFloor
		};
	},
	[types.ADD_DELIVERY_TIME] (state, deliveryTime) {
		state.persistedData.deliveryDetails = {
			...state.persistedData.deliveryDetails,
			deliveryTime
		};
	},
	[types.ADD_TEMPORARY_ADDRESS](state, deliveryTempAddress){
		state.persistedData.temporaryDeliveryAddress = deliveryTempAddress;
	},
	[types.GATHER_EVALUATION_TAG_DATA] (state, customerEvaluationData) {
		state.persistedData = {
			...state.persistedData,
			customerEvaluationData
		};
	},
	[types.STORE_TARGET_GROUP_DATA] (state, targetGroupData) {
		state.persistedData = {
			...state.persistedData,
			targetGroupData
		};
	},
	[types.SET_SUNSET_POINTS] (state, sunsetPointsData) {
		state.persistedData.sunsetPoints = sunsetPointsData;
	},
	[types.SET_GROUP_ORDER] (state, groupOrderId = "") {
		state.persistedData.newCartData.groupOrderId = groupOrderId;
	},
	[types.GROUP_ORDER_IS_USER_OWNER] (state, isOwner) {
		state.persistedData.newCartData.isUserGroupOrderOwner = isOwner;
	},
	[types.UPDATE_GROUP_ORDER_FRIENDS_ORDERS] (state, orderItems) {
		if (!orderItems) {
			return;
		}
		const data = state.persistedData.newCartData.groupOrderFriendsOrderData;
		if (!data.length && !orderItems.length) {
			return;
		}
		if (!orderItems.length) {
			data.splice(0, data.length);
			return;
		}
		if (orderItems.length < data.length) {
			const ids = orderItems.map(x => x.id);
			const newData = data.filter(x => ids.includes(x.id));
			data.splice(0, data.length);
			data.push(...newData);
		} else {
			const groupOrderStateIds = data.map(x => x.id);
			const diff = orderItems.filter(x => !groupOrderStateIds.includes(x.id));
			if (diff.length) {
				data.push(...diff);
			}
		}
	},

	/**
	 * this is shared basket related, and gt2 only. This is related to product catalog page and to show only user (owner) selection, and not selection from all friends + user's selection.
	 */
	[types.FILTER_ONLY_OWNERS_PRODUCT_SELECTIONS] (state) {
		const groupData = state.persistedData.newCartData.groupOrderFriendsOrderData;
		const groupProdSelIds = groupData.map(x => x.productSelections).flat().map(x => x.productSelectionId);
		state.persistedData.newCartData.productSelections = state.persistedData.newCartData.productSelections.filter(x => !groupProdSelIds.includes(x.productSelectionId));
	},
	[types.RESET_GROUP_ORDER_STATE] (state) {
		state.persistedData.newCartData.groupOrderId = "";
		state.persistedData.newCartData.isUserGroupOrderOwner = false;
		state.persistedData.newCartData.groupOrderFriendsOrderData = [];
		state.persistedData.newCartData.productSelections = [];
	},

	[types.SET_QR_CODE_TABLE_SERVING_DATA] (state, payload) {
		state.persistedData.newCartData.qrCodeTableServingData = payload;
	},

	[types.ADD_FRIEND_INVITE_CODE_TO_ORDER] (state, payload) {
		state.persistedData.friendInviteCode = payload;
	},

	[types.SET_VOUCHER_PRODUCT_ADD_TO_CART] (state, payload) {
		state.persistedData.voucherReward = payload;
	},

	[types.SET_USER_CRM_USED_VOUCHERS] (state, payload) {
		state.persistedData.userCrmVouchers = payload;
	},
	[types.SET_PRODUCT_IDS_FOR_RESELECTION] (state, payload) {
		state.persistedData.menuProductIdsForReselection = payload;
	},
	[types.SET_UNAVAILABLE_PRODUCT_IDS] (state, payload) {
		state.persistedData.unavailableProductIds = payload;
	},
	[types.SET_SINGLE_MAIN_PRODUCT_ID_TO_MENU_PRODUCT_ID_MAP] (state, payload) {
		state.persistedData.singleMainProductIdsToMenuIdsMap = payload;
	},
	[types.SET_UPGRADE_TO_MENU_FROM_BASKET_FLAG] (state, payload) {
		state.persistedData.upgradeToMenuFromBasketFlag = payload;
	},
	[types.SET_NEW_BASKET_STEPPER_TO_TABLE_SERVING_FLAG] (state, payload) {
		state.persistedData.newBasketStepperToTableServingFlag = payload;
	},
	[types.SET_PRODUCT_SELECTION_TO_REMOVE] (state, payload) {
		state.persistedData.productSelectionIdToRemove = payload;
	},
	[types.SET_MARKETING_CARD_ON_CLICK_DATA] (state, payload) {
		if (!payload) {
			// reset
			state.marketingCardDataOnClick.productId = null;
			state.marketingCardDataOnClick.category = null;
			state.marketingCardDataOnClick.upgradeToMenuDialog = false;
		} else {
			state.marketingCardDataOnClick = payload;
		}
	},
	[types.SET_RCM_MARKETING_CARD] (state,  payload) {
		if (!payload) {
			state.rcmMarketingCard.productIds = [];
			state.rcmMarketingCard.enable = false;
		} else {
			Object.assign(state.rcmMarketingCard, payload);
		}
	},
	[types.SET_LAST_ADDED_PRODUCT_SELECTION_FOR_ADD_DIPS_DIALOG] (state, payload) {
		state.lastProductSelectionForAddDipsDialog = payload;
	},
	[types.POST_PURCHASE_DISCOUNT_PRODUCT_SELECTIONS] (state, payload) {
		state.persistedData.ppdProductSelectionIds = payload;
	}
};

export default CartMutations;
